import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Alert } from "reactstrap";
import { ThreeDots } from "react-loader-spinner";

import useStoreCameraTree from "../cameraPrototypes/shared/useStoreCameraTree";
import useInferenceMachineCache from "../cameraPrototypes/shared/useInferenceMachineCache";
import { getRecorderBrands } from "../cameraPrototypes/shared/captureDeviceService";
import CameraModelDvrPanel from "./CameraModelDvrPanel";

/**
 * Camera-model-native variant of the store DVR-capabilities (dashcam) page.
 *
 * Reads the Store → CaptureDevice → Camera tree and renders one full-parity
 * config panel per device, organized around cameras/views instead of the legacy
 * VideoRecorder/Channel model. Brand ids are resolved to kinds and inference-machine
 * ids to hostnames (both needed by the reshape that feeds capabilitiesService).
 */
export default function CameraModelCapabilities({ storeId, localInference }) {
  const { tree, loading, error } = useStoreCameraTree(storeId);
  const machineCache = useInferenceMachineCache(tree);
  const [brandKindById, setBrandKindById] = useState({});
  // Brand resolution is not cosmetic: an unresolved brand reads as "not an IP camera", which
  // shows the bulk-config, DHCP and configured-stream controls the legacy panel deliberately
  // hides for IP cameras. So the panels wait for it, and a failure is surfaced rather than
  // silently degrading every device to the non-IP layout.
  const [brandsState, setBrandsState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    getRecorderBrands()
      .then((brands) => {
        if (cancelled) return;
        const map = {};
        (Array.isArray(brands) ? brands : []).forEach((b) => {
          if (b && b.id != null) map[b.id] = b.kind ?? null;
        });
        setBrandKindById(map);
        setBrandsState("loaded");
      })
      .catch(() => {
        if (cancelled) return;
        setBrandsState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Strip the "#<id>" unresolved placeholder the cache uses for display — the reshape
  // feeds this as `machine_name` for the VPN lookup, and a "#id" is not a real hostname
  // (empty string means "no tunnel / direct access", matching the legacy adapter).
  const hostnameByMachineId = useMemo(() => {
    const out = {};
    Object.entries(machineCache || {}).forEach(([id, host]) => {
      out[id] = host && !String(host).startsWith("#") ? host : "";
    });
    return out;
  }, [machineCache]);

  const machineIds = useMemo(() => {
    const set = new Set();
    (tree || []).forEach((device) =>
      (device.cameras || []).forEach((camera) => {
        if (camera.inference_machine != null) set.add(camera.inference_machine);
      })
    );
    return [...set];
  }, [tree]);

  if (loading || brandsState === "loading") {
    return (
      <div className="content">
        <ThreeDots height="20" width="40" radius="5" color="#1d8cf8" ariaLabel="loading" visible />
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <Alert color="danger">Failed to load the camera-model tree.</Alert>
      </div>
    );
  }

  const devices = tree || [];

  return (
    <div className="content">
      {brandsState === "error" && (
        <Alert color="warning">
          Could not load recorder brands — devices may be shown with the wrong controls (IP cameras rendered as DVRs).
          Reload before changing any configuration.
        </Alert>
      )}
      {machineIds.length > 1 && (
        <Alert color="warning">
          This store spans {machineIds.length} inference machines — each device is configured via its own machine.
        </Alert>
      )}
      {devices.length === 0 && <p className="text-muted">No capture devices for this store.</p>}
      {devices.map((device) => (
        <CameraModelDvrPanel
          key={device.public_id}
          device={device}
          brandKindById={brandKindById}
          hostnameByMachineId={hostnameByMachineId}
          localInference={localInference}
          storeId={storeId}
        />
      ))}
    </div>
  );
}

CameraModelCapabilities.propTypes = {
  storeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  localInference: PropTypes.bool,
};
